import { Inbox, MoreHorizontal, Users } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { cn } from "@/lib/utils";

/**
 * Route patterns (Next.js `router.pathname`) that hide the bottom tab bar on
 * mobile. These are immersive, full-screen builders/wizards that render their
 * own chrome with an explicit back/close affordance (BuilderHeader,
 * CreateHeader, flow editor header) and need the whole viewport, mirroring
 * how modally-presented screens cover the tab bar in the iOS app.
 *
 * Every other page rendered through MainLayout shows the tab bar by default,
 * so new pages can't accidentally fall back to legacy mobile chrome. Adding a
 * route here requires classifying it in
 * `src/components/__tests__/mobile-chrome.test.ts` too — the test enumerates
 * `pages/` and fails on unclassified or stale routes.
 */
export const IMMERSIVE_PATHNAMES = new Set([
  "/blasts/new",
  "/blasts/[id]/edit",
  "/flows/[id]/edit",
  "/forms/new",
  "/forms/[id]",
  "/forms/[id]/edit",
  "/landing-pages/new",
  "/landing-pages/[id]",
  "/lists/new/list",
  "/contacts/import",
  "/templates/new",
  "/templates/[id]/edit",
]);

export function isTabBarPathname(pathname: string): boolean {
  return !IMMERSIVE_PATHNAMES.has(pathname);
}

/**
 * Height the fixed tab bar occupies, exposed so layouts can subtract it from
 * full-height pages (see `--app-tab-bar-h` in globals.css / MainLayout).
 */
export const TAB_BAR_HEIGHT = "calc(3.5rem + env(safe-area-inset-bottom))";

interface TabItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  isActive: (pathname: string) => boolean;
}

function isInboxPath(pathname: string): boolean {
  return (
    pathname.startsWith("/messages") ||
    pathname.startsWith("/calls") ||
    pathname.startsWith("/tasks")
  );
}

const PRIMARY_TABS: TabItem[] = [
  { name: "Inbox", href: "/messages", icon: Inbox, isActive: isInboxPath },
  { name: "Contacts", href: "/contacts", icon: Users, isActive: (p) => p.startsWith("/contacts") },
];

/**
 * Bottom tab bar for mobile (`md:hidden`). Primary destinations are
 * Inbox · Contacts · More. Chats, Calls, and Tasks live under Inbox.
 * Everything else is under More, which is why More is "active" for any
 * other tab-bar route (blasts, analytics, settings, ...).
 */
export function MobileTabBar({ unreadCount }: { unreadCount?: number }) {
  const router = useRouter();

  const moreActive = !PRIMARY_TABS.some((tab) => tab.isActive(router.pathname));

  return (
    <nav
      aria-label="Primary"
      className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex h-14 items-stretch">
        {PRIMARY_TABS.map((tab) => (
          <TabLink
            key={tab.href}
            name={tab.name}
            href={tab.href}
            icon={tab.icon}
            active={tab.isActive(router.pathname)}
            badge={tab.href === "/messages" ? unreadCount : undefined}
          />
        ))}
        <TabLink name="More" href="/more" icon={MoreHorizontal} active={moreActive} />
      </div>
    </nav>
  );
}

function TabLink({
  name,
  href,
  icon: Icon,
  active,
  badge,
}: {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  badge?: number;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className="flex flex-1 items-center justify-center"
    >
      {/* Active state is a capsule (fully rounded sides, iOS-style) around
          the icon + label. whitespace-nowrap keeps the label from wrapping in
          narrow tab slots, so the pill stays one clean capsule at any width. */}
      <span
        className={cn(
          "flex flex-col items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 transition-colors",
          active ? "bg-muted text-foreground" : "text-muted-foreground",
        )}
      >
        <span className="relative">
          <Icon className="size-5" aria-hidden="true" />
          {badge && badge > 0 ? (
            <span
              aria-label={`${badge} unread`}
              className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground dark:bg-brand-accent dark:text-brand-accent-foreground"
            >
              {badge > 20 ? "20+" : badge}
            </span>
          ) : null}
        </span>
        <span className="text-[10px] font-medium leading-none">{name}</span>
      </span>
    </Link>
  );
}
